import { Injectable, inject, resource, signal } from '@angular/core';
import { AdminUnpricedItem } from './admin-unpriced.interface';
import { ToastrService } from 'ngx-toastr';
import { SocketService } from '../../../core/services/socket.service';

import { PaginationState } from '@tanstack/angular-table';
import { ColumnDef } from '@tanstack/angular-table';
import { adminUnpricedTableConfig } from './admin-unpriced.config';
import { ModalService } from '@features/modals/services/modal.service';

enum OrderOptions {
  asc,
  desc,
}

enum OrderFieldOption {
  default,
  first_popup,
  last_popup,
  loads,
}

interface SelectOptionConfig<TEnum> {
  label: string;
  key: keyof TEnum;
  fn: () => void;
}

interface CategoryOption {
  text: string;
  value: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdminUnpricedService {
  private toastrService = inject(ToastrService);
  private socketService = inject(SocketService);
  private modalService = inject(ModalService);

  public readonly categoryOptions = signal<CategoryOption[]>([
    // { text: 'load all', value: 'all' },
    // { text: 'load unusuals', value: 'unusual' },
    // { text: 'load strange', value: 'strange' },
    // { text: 'load pure', value: 'pure' },
    { text: 'load collectors', value: 'collectors' },
    { text: 'load genuine', value: 'genuine' },
    { text: 'load vintage', value: 'vintage' },
    { text: 'load haunted', value: 'haunted' },
    { text: 'load strange', value: 'strange' },
    { text: 'load unique', value: 'unique' },
    { text: 'load unusual', value: 'unusual' },
    { text: 'load unusual taunt', value: 'unusual taunt' },
  ]);
  public readonly categoryOption = signal<CategoryOption>(
    this.categoryOptions()[0]
  );

  readonly columns: ColumnDef<AdminUnpricedItem>[] = adminUnpricedTableConfig({
    handleRemoveItem: (item) => {
      this.handleRemoveItem(item);
    },
    handleOpenEditModal: (item) => {
      this.handleOpenEditModal(item);
    },
  });

  readonly pagination = signal<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  readonly orderOptions: SelectOptionConfig<typeof OrderOptions>[] = [
    {
      label: 'ASC',
      key: 'asc',
      fn: () => {
        this.order.set(this.orderOptions[0]);
      },
    },
    {
      label: 'DESC',
      key: 'desc',
      fn: () => {
        this.order.set(this.orderOptions[1]);
      },
    },
  ];
  readonly order = signal<SelectOptionConfig<typeof OrderOptions>>(
    this.orderOptions[0]
  );
  readonly orderFieldOptions: SelectOptionConfig<typeof OrderFieldOption>[] = [
    {
      label: 'DEFAULT',
      key: 'default',
      fn: () => {
        this.orderField.set(this.orderFieldOptions[0]);
      },
    },
    {
      label: 'LAST POPUP',
      key: 'last_popup',
      fn: () => {
        this.orderField.set(this.orderFieldOptions[1]);
      },
    },
    {
      label: 'FIRST POPUP',
      key: 'first_popup',
      fn: () => {
        this.orderField.set(this.orderFieldOptions[2]);
      },
    },
    {
      label: 'LOADS',
      key: 'loads',
      fn: () => {
        this.orderField.set(this.orderFieldOptions[3]);
      },
    },
  ];
  readonly orderField = signal<SelectOptionConfig<typeof OrderFieldOption>>(
    this.orderFieldOptions[0]
  );

  readonly searchTextApi = signal<string>('');
  readonly searchText = signal<string>('');

  public readonly itemsUnpricedResource = resource({
    params: () => {
      const order = this.order().key;
      const sort = this.orderField().key;
      const text = this.searchTextApi();
      const page = this.pagination().pageIndex + 1;
      const category = this.categoryOption().value;

      return {
        page: page,
        name: text,
        order,
        category,
        sort,
      };
    },
    loader: async (params) => {
      const { page, name, order, sort, category } = params.params;
      const response: AdminUnpricedItem[] = await this.socketService.emit(
        'admin_load_unpriced',
        {
          page,
          name,
          order,
          sort,
          category,
        }
      );

      return response;
    },
  });

  public handlePaginationChange(action: 'prev' | 'next' | 'reset') {
    this.pagination.update((state) => {
      return {
        ...state,
        pageIndex:
          action === 'reset'
            ? 0
            : action === 'prev'
            ? state.pageIndex - 1
            : state.pageIndex + 1,
      };
    });
    this.toastrService.success(
      `Page changed to ${this.pagination().pageIndex + 1}`,
      'Pagination'
    );
  }

  public handleResetFilters() {
    this.searchTextApi.set('');
    this.searchText.set('');
    this.toastrService.success('Filters have been reset', 'Filters');
  }

  public handleResetOrders() {
    this.order.set(this.orderOptions[0]);
    this.toastrService.success('Orders have been reset', 'Orders');
  }

  public handleResetAll() {
    this.handleResetFilters();
    this.handleResetOrders();
    this.handlePaginationChange('reset');
    this.toastrService.success(
      'All filters and orders have been reset',
      'Reset'
    );
  }

  public onSearchSubmit() {
    this.searchTextApi.set(this.searchText());
  }

  public onSearchClear() {
    this.searchTextApi.set('');
  }

  public async handleRemoveItem(item: AdminUnpricedItem) {
    try {
      await this.socketService.emit('admin_remove_unpriced', {
        name: item.name,
      });
      this.itemsUnpricedResource.reload();
      this.toastrService.success(`Item ${item.name} removed`, 'Remove');
    } catch (error) {
      this.toastrService.error(`Failed to remove item ${item.name}`, 'Remove');
    }
  }

  public async editUnpricedItem(name: string, price: number): Promise<boolean> {
    const success = await this.socketService.emit('admin_edit_unpriced', {
      name,
      price,
    });
    return success;
  }

  public handleOpenEditModal(item?: AdminUnpricedItem) {
    this.modalService.openModal('AdminUnpricedModal', {
      item,
    });
  }
}
